import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const API_URL = API_BASE_URL;

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  organizationId: string;
  organizationName: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string, orgName: string, invitationToken?: string) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('authToken'));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user && typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', 'G-H2BES4ZMVF', {
        user_id: user.id,
      });
      window.gtag('set', 'user_properties', {
        organization_id: user.organizationId,
        organization_name: user.organizationName,
        role: user.role
      });
    }
  }, [user]);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  async function fetchCurrentUser() {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const userData = response.data.data;
        setUser({
          id: userData.id,
          email: userData.email,
          name: userData.name,
          role: userData.role,
          organizationId: userData.organization.id,
          organizationName: userData.organization.name
        });
      }
    } catch {
      localStorage.removeItem('authToken');
      setToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }

  async function login(email: string, password: string) {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    });

    if (response.data.success) {
      const { token, user } = response.data;
      localStorage.setItem('authToken', token);
      setToken(token);
      setUser(user);
    } else {
      throw new Error(response.data.error || 'Login failed');
    }
  }

  async function signup(
    email: string,
    password: string,
    name: string,
    organizationName: string,
    invitationToken?: string
  ) {
    const response = await axios.post(`${API_URL}/api/auth/signup`, {
      email,
      password,
      name,
      organizationName,
      inviteToken: invitationToken
    });

    if (response.data.success) {
      const { token, user } = response.data;
      localStorage.setItem('authToken', token);
      setToken(token);
      setUser(user);
    } else {
      throw new Error(response.data.error || 'Signup failed');
    }
  }

  async function logout() {
    try {
      if (token) {
        await axios.post(`${API_URL}/api/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch {
      // Logout failure is non-critical — clear local state regardless
    } finally {
      localStorage.removeItem('authToken');
      setToken(null);
      setUser(null);
      window.location.href = '/login';
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        signup,
        logout,
        isLoading,
        isAuthenticated: !!user
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
